import React, { useEffect, useMemo, useState } from 'react';
import { Popup, Toast } from 'antd-mobile';
import axios from 'axios';
import API_ROUTES from '../../../api';
import Icon from '../../../icon';
import CardReasonPicker from './CardReasonPicker';

const ACTIONS = [
    { value: 3, label: '进球记录', icon: Icon.GOAL, tone: 'goal' },
    { value: 4, label: '乌龙球', icon: Icon.OWN_GOAL, tone: 'own-goal' },
    { value: 5, label: '黄牌', icon: Icon.YELLOW_CARD, tone: 'yellow' },
    { value: 6, label: '红牌', icon: Icon.RED_CARD, tone: 'red' },
    { value: 7, label: '换人', icon: Icon.UP_AND_DOWN_ICON, tone: 'swap' },
    { value: 0, label: '受伤', icon: Icon.SHOUSHANG, tone: 'injury' },
];

const OPEN_PLAY_GOALS = [
    { value: 13, label: '左脚进球', icon: '⚽' },
    { value: 14, label: '右脚进球', icon: '⚽' },
    { value: 11, label: '头球进球', icon: '🧠' },
    { value: 3, label: '其他方式', icon: '✨' },
];

const SET_PLAY_GOALS = [
    { value: 19, label: '任意球·左脚', icon: '🎯' },
    { value: 15, label: '任意球·右脚', icon: '🎯' },
    { value: 20, label: '角球·左脚', icon: '🚩' },
    { value: 16, label: '角球·右脚', icon: '🚩' },
    { value: 21, label: '点球·左脚', icon: '🥅' },
    { value: 12, label: '点球·右脚', icon: '🥅' },
];

const MemberChoiceGrid = ({ members, selectedId, getMemberImage, onSelect, noneLabel }) => (
    <div className="pitch-action-member-grid">
        {noneLabel && (
            <button
                type="button"
                className={`pitch-action-member-option none${selectedId === null ? ' active' : ''}`}
                onClick={() => onSelect(null)}
            >
                <span className="pitch-action-none-icon">—</span>
                <strong>{noneLabel}</strong>
                {selectedId === null && <em>✓</em>}
            </button>
        )}
        {members.map(member => (
            <button
                key={member.id}
                type="button"
                className={`pitch-action-member-option${selectedId === member.id ? ' active' : ''}`}
                onClick={() => onSelect(member.id)}
            >
                <img src={getMemberImage(member)} alt="" />
                <strong>{member.name}</strong>
                {selectedId === member.id && <em>✓</em>}
            </button>
        ))}
    </div>
);

// 球场头像的所有比赛事件都在同一个底部面板内完成，避免逐层弹窗。
const PitchPlayerActionPopup = ({
    context,
    gameDetail,
    memberListInfo,
    getMemberImage,
    onClose,
    onUpdated,
}) => {
    const visible = Boolean(context);
    const player = context?.player;
    const gameTeam = context?.gameTeam;
    const [action, setAction] = useState(null);
    const [goalEventId, setGoalEventId] = useState(null);
    const [assistMemberId, setAssistMemberId] = useState('1');
    const [replacementId, setReplacementId] = useState(undefined);
    const [cardReason, setCardReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setAction(null);
        setGoalEventId(null);
        setAssistMemberId('1');
        setReplacementId(undefined);
        setCardReason('');
        setCustomReason('');
        setSubmitting(false);
    }, [visible, player?.id]);

    const assistingMembers = useMemo(() => (
        (memberListInfo || []).filter(item =>
            item.gameTeam === gameTeam &&
            item.firstTemp !== null &&
            item.id !== player?.id
        )
    ), [gameTeam, memberListInfo, player?.id]);

    const substituteMembers = useMemo(() => (
        (memberListInfo || []).filter(item => item.firstTemp === null && item.gameTeam === gameTeam)
    ), [gameTeam, memberListInfo]);

    const isOpenPlayGoal = OPEN_PLAY_GOALS.some(item => item.value === goalEventId);
    const selectedAction = ACTIONS.find(item => item.value === action);
    const selectedGoal = [...OPEN_PLAY_GOALS, ...SET_PLAY_GOALS].find(item => item.value === goalEventId);
    const selectedAssist = assistingMembers.find(item => item.id === assistMemberId);
    const selectedReplacement = substituteMembers.find(item => item.id === replacementId);
    const finalCardReason = customReason.trim() || cardReason;

    const chooseAction = value => {
        setAction(value);
        setGoalEventId(null);
        setAssistMemberId('1');
        setReplacementId(undefined);
        // 切换记录类型时清空上一次选择的红黄牌原因。
        setCardReason('');
        setCustomReason('');
    };

    const submitEvent = async () => {
        if (action === null) {
            Toast.show({ icon: 'fail', content: '请选择需要记录的事件' });
            return;
        }
        if (action === 3 && !goalEventId) {
            Toast.show({ icon: 'fail', content: '请选择进球方式' });
            return;
        }
        if (action === 7 && replacementId === undefined) {
            Toast.show({ icon: 'fail', content: '请选择替补球员或仅下场' });
            return;
        }
        if ([5, 6].includes(action) && !finalCardReason) {
            Toast.show({ icon: 'fail', content: '请选择或输入犯规原因' });
            return;
        }

        let data;
        if (action === 3) {
            data = {
                gameId: gameDetail.id,
                memberId: player.id,
                eventId: goalEventId,
                ...(isOpenPlayGoal ? { assistMemberId: assistMemberId ?? '1' } : {}),
            };
        } else if (action === 7) {
            let currentModel = [];
            try {
                const modelValue = gameTeam === 1 ? gameDetail?.greenCurrentModel : gameDetail?.redCurrentModel;
                currentModel = modelValue ? JSON.parse(modelValue) : [];
                if (!Array.isArray(currentModel)) currentModel = [];
            } catch (error) {
                currentModel = [];
            }

            // 按当前球场格子更新阵型；选择“仅下场”时直接移除该位置。
            const positionIndex = currentModel.findIndex(item =>
                item.line === context.line && item.team === gameTeam && item.pos === context.pos
            );
            if (replacementId === null) {
                if (positionIndex !== -1) currentModel.splice(positionIndex, 1);
            } else {
                const nextPosition = {
                    line: context.line,
                    team: gameTeam,
                    pos: context.pos,
                    id: replacementId,
                };
                if (positionIndex !== -1) currentModel[positionIndex] = nextPosition;
                else currentModel.push(nextPosition);
            }

            data = {
                gameId: gameDetail.id,
                memberId: replacementId,
                eventId: '17',
                eventInfo: '',
                upDownMemberId: player.id,
                [gameTeam === 1 ? 'greenCurrentModel' : 'redCurrentModel']: JSON.stringify(currentModel),
            };
        } else {
            data = {
                team: action,
                gameId: gameDetail.id,
                memberId: player.id,
                eventId: action,
                eventInfo: [5, 6].includes(action) ? finalCardReason : '',
            };
        }

        setSubmitting(true);
        try {
            const response = await axios.post(API_ROUTES.game_event, data, {});
            if (response.data.code === 200) {
                Toast.show({ icon: 'success', content: '记录成功！' });
                onClose();
                onUpdated();
            } else {
                Toast.show({ icon: 'fail', content: response.data.msg });
            }
        } catch (error) {
            Toast.show({ icon: 'fail', content: '记录失败，请联系管理员！' });
        } finally {
            setSubmitting(false);
        }
    };

    const summaryText = (() => {
        if (!selectedAction) return '请选择本次要记录的事件';
        if (action === 3) {
            if (!selectedGoal) return `${player?.name} · 待选择进球方式`;
            return `${player?.name} · ${selectedGoal.label}${isOpenPlayGoal ? ` · 助攻：${selectedAssist?.name || '无'}` : ''}`;
        }
        if (action === 7) {
            if (replacementId === undefined) return `${player?.name} · 待选择替补球员`;
            return replacementId === null
                ? `${player?.name}下场 · 暂不换入球员`
                : `${player?.name}下场 · ${selectedReplacement?.name || ''}上场`;
        }
        if ([5, 6].includes(action)) {
            return `${player?.name} · ${selectedAction.label} · ${finalCardReason || '待选择原因'}`;
        }
        return `${player?.name} · ${selectedAction.label}`;
    })();

    return (
        <Popup
            visible={visible}
            onMaskClick={onClose}
            bodyStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '92vh', overflow: 'hidden' }}
            style={{ '--z-index': 1400 }}
        >
            <div className="pitch-action-sheet">
                <div className="pitch-action-header">
                    <button type="button" className="pitch-action-cancel" onClick={onClose}>取消</button>
                    <div className="pitch-action-player">
                        {player && <img src={getMemberImage(player)} alt="" />}
                        <div>
                            <strong>{player?.name || '球员事件'}</strong>
                            <span>比赛数据记录</span>
                        </div>
                    </div>
                    <button type="button" className="pitch-action-confirm" disabled={submitting} onClick={submitEvent}>
                        {submitting ? '提交中' : '确认'}
                    </button>
                </div>

                <div className="pitch-action-content">
                    <div className="pitch-action-section-title"><span>1</span>选择记录类型</div>
                    <div className="pitch-action-grid">
                        {ACTIONS.map(item => (
                            <button
                                key={item.value}
                                type="button"
                                className={`${item.tone}${action === item.value ? ' active' : ''}`}
                                onClick={() => chooseAction(item.value)}
                            >
                                <img src={item.icon} alt="" />
                                <strong>{item.label}</strong>
                                {action === item.value && <em>✓</em>}
                            </button>
                        ))}
                    </div>

                    {action === 3 && (
                        <div className="pitch-action-detail">
                            <div className="pitch-action-section-title"><span>2</span>选择进球方式</div>
                            <div className="pitch-action-group-label">运动战</div>
                            <div className="pitch-goal-option-grid">
                                {OPEN_PLAY_GOALS.map(item => (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className={goalEventId === item.value ? 'active' : ''}
                                        onClick={() => {
                                            setGoalEventId(item.value);
                                            setAssistMemberId('1');
                                        }}
                                    >
                                        <span>{item.icon}</span><strong>{item.label}</strong>
                                        {goalEventId === item.value && <em>✓</em>}
                                    </button>
                                ))}
                            </div>

                            <div className="pitch-action-group-label">定位球</div>
                            <div className="pitch-goal-option-grid">
                                {SET_PLAY_GOALS.map(item => (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className={goalEventId === item.value ? 'active' : ''}
                                        onClick={() => {
                                            setGoalEventId(item.value);
                                            setAssistMemberId('1');
                                            if ([12, 21].includes(item.value)) {
                                                Toast.show({ content: '点球也可以使用右侧固定的点球记录入口', duration: 2200 });
                                            }
                                        }}
                                    >
                                        <span>{item.icon}</span><strong>{item.label}</strong>
                                        {goalEventId === item.value && <em>✓</em>}
                                    </button>
                                ))}
                            </div>

                            {isOpenPlayGoal && (
                                <div className="pitch-action-assist-section">
                                    <div className="pitch-action-section-title"><span>3</span>选择助攻球员</div>
                                    <MemberChoiceGrid
                                        members={assistingMembers}
                                        selectedId={assistMemberId === '1' ? null : assistMemberId}
                                        getMemberImage={getMemberImage}
                                        onSelect={value => setAssistMemberId(value === null ? '1' : value)}
                                        noneLabel="无助攻"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {action === 7 && (
                        <div className="pitch-action-detail">
                            <div className="pitch-action-section-title"><span>2</span>选择替补上场球员</div>
                            <MemberChoiceGrid
                                members={substituteMembers}
                                selectedId={replacementId}
                                getMemberImage={getMemberImage}
                                onSelect={setReplacementId}
                                noneLabel="仅下场"
                            />
                            {substituteMembers.length === 0 && (
                                <div className="pitch-action-empty-note">当前没有替补球员，可以选择“仅下场”。</div>
                            )}
                        </div>
                    )}

                    {[5, 6].includes(action) && (
                        <div className="pitch-action-detail">
                            <CardReasonPicker
                                cardReason={cardReason}
                                customReason={customReason}
                                onReasonChange={setCardReason}
                                onCustomChange={setCustomReason}
                                danger={action === 6}
                            />
                        </div>
                    )}

                    {action !== null && ![3, 5, 6, 7].includes(action) && (
                        <div className={`pitch-action-simple-note ${selectedAction?.tone || ''}`}>
                            <img src={selectedAction?.icon} alt="" />
                            <div>
                                <strong>记录{selectedAction?.label}</strong>
                                <span>确认后将为 {player?.name} 添加本次比赛事件。</span>
                            </div>
                        </div>
                    )}

                    <div className="pitch-action-summary">{summaryText}</div>
                </div>
            </div>
        </Popup>
    );
};

export default PitchPlayerActionPopup;
